import { getVariantPrice } from "@babybjorn/utils/commercetools/money";
import { graphql, useStaticQuery } from "gatsby";
import { compact, uniqBy } from "lodash";
import { getStoryblokLanguage } from "../../config/storyblok-config";
import { useAppState } from "../context/app/context";
import { formatVariantPrice } from "../utils/cart/currency";
import { getSlugForProductVariant } from "../utils/storyblok/product-variant";

type GiftCard = {
	id: string;
	amount: string;
	url: string;
	price: number;
};

export const useProductGiftCards = () => {
	const { siteConfig, userCountry } = useAppState();
	const storyblokGiftCards = useStaticQuery<Queries.StoryblokGiftCardsQuery>(
		graphql`
			query StoryblokGiftCards {
				allStoryblokEntry(
					filter: {
						commercetools_product: {
							productType: { obj: { key: { eq: "gift-card" } } }
						}
					}
				) {
					nodes {
						id
						lang
						name
						slug
						commercetools_product {
							...CommerceToolsProduct
						}
					}
				}
			}
		`,
	).allStoryblokEntry.nodes.filter(
		(product) => product.lang === getStoryblokLanguage(),
	);

	const rawGiftCards = storyblokGiftCards
		.reduce<GiftCard[]>((cards, giftCard) => {
			const masterVariant = giftCard.commercetools_product?.masterVariant;
			const variants = giftCard.commercetools_product?.variants || [];

			const allVariants = masterVariant
				? [masterVariant, ...variants]
				: variants;

			const variantGiftCards: GiftCard[] = compact(
				allVariants.map((variant) => {
					const url = getSlugForProductVariant(variant);
					const id = variant.id;
					const price = getVariantPrice(
						variant,
						siteConfig.currency,
						userCountry,
					).centAmount;

					if (url && price) {
						const amount = formatVariantPrice(
							variant,
							siteConfig.currency,
							userCountry,
						);
						return { amount, url, id, price };
					}
					return;
				}),
			);

			return [...cards, ...variantGiftCards];
		}, [])
		.sort((a, b) => a.price - b.price);

	const giftCards = uniqBy(rawGiftCards, "price");

	return { giftCards };
};
